import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { ELinksName, EParams } from 'services/constants/list-links-sidebar';

import Loading from 'ui/Loading/Loading';
import AuthLayout from '../layouts/AuthLayout';
import ErrorRoutes from './ErrorRoutes';
import ProtectedRoute from './ProtectedRoute';

// Хелпер для зручного обгортання lazy-компонента в Suspense
const withSuspense = (Node: React.ReactNode) => (
  <Suspense
    fallback={
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
        }}
      >
        <Loading size={12} color="#4f46e5" inline={false} />
      </div>
    }
  >
    {Node}
  </Suspense>
);

// ===== Lazy-імпорти сторінок =====
const Dashboard = lazy(() => import('pages/Dashboard/Dashboard'));
const Channels = lazy(() => import('pages/Dashboard/Channels'));
const ChannelsDetailsUser = lazy(() => import('pages/Dashboard/ChannelsDetailsUser'));
const Reports = lazy(() => import('pages/Dashboard/Reports'));
const Transaction = lazy(() => import('pages/Dashboard/Transaction'));
const UserDetails = lazy(() => import('pages/Dashboard/UserDetails'));
const Users = lazy(() => import('pages/Dashboard/Users'));
const Groups = lazy(() => import('pages/Dashboard/Groups'));
const Admins = lazy(() => import('pages/Dashboard/Admins/Admins'));
const Finance = lazy(() => import('pages/Dashboard/Finance/Finance'));
const Monetization = lazy(() => import('pages/Dashboard/Monetization/Monetization'));
const AdvertisingAnalytics = lazy(
  () => import('pages/Dashboard/AdvertisingAnalytics/AdvertisingAnalytics'),
);
const NameExemple = lazy(() => import('pages/Dashboard/NameExemple/NameExemple'));
const PostDetails = lazy(() => import('pages/Dashboard/PostDetails/PostDetails'));
const MediaAndFilesDetails = lazy(
  () => import('pages/Dashboard/MediaAndFilesDetails/MediaAndFilesDetails'),
);
const GroupsDetails = lazy(() => import('pages/Dashboard/GroupsDetails/GroupsDetails'));
const AiServices = lazy(() => import('pages/Dashboard/AiServices/AiServices'));
const MonetizationDetailsPage = lazy(
  () => import('pages/Dashboard/MonetizationDetails/MonetizationDetails'),
);
const AdminDetails = lazy(() => import('pages/Dashboard/AdminDetails/AdminDetails'));
const Reserved = lazy(() => import('pages/Dashboard/Reserved/Reserved'));
const RegistrationRequests = lazy(
  () => import('pages/Dashboard/RegistrationRequests/RegistrationRequests'),
);
const ReportsDetails = lazy(() => import('pages/Dashboard/ReportsDetails/ReportsDetails'));
const Content = lazy(() => import('pages/Dashboard/Content/Content'));
const TrackDetails = lazy(() => import('pages/Dashboard/TrackDetails/TrackDetails'));
const AlbumDetails = lazy(() => import('pages/Dashboard/AlbumDetails/AlbumDetails'));
const EditTrack = lazy(() => import('pages/Dashboard/EditTrack/EditTrack'));
const SystemChannels = lazy(() => import('pages/Dashboard/SystemChannels/SystemChannels'));
const TermManagement = lazy(() => import('pages/Dashboard/TermManagement/TermManagement'));
const TermManagementChange = lazy(
  () => import('pages/Dashboard/TermManagementChange/TermManagementChange'),
);
const ElloPartners = lazy(() => import('pages/Dashboard/ElloPartners/ElloPartners'));
const RequestDetails = lazy(
  () => import('components/ElloPartnersComponents/RequestDetails/RequestDetails'),
);
const ManagementDetails = lazy(
  () => import('components/ElloPartnersComponents/ManagementDetails/ManagementDetails'),
);
const ApkFile = lazy(() => import('pages/ApkFile/ApkFile'));
const UserPartner = lazy(() => import('pages/Dashboard/UserPartner/UserPartner'));
const AiChatBot = lazy(() => import('pages/Dashboard/AiChatBot/AiChatBot'));
const Reactions = lazy(() => import('pages/Dashboard/Reactions/Reactions'));
const AddReaction = lazy(() => import('components/ReactionsComponent/AddReaction/AddReaction'));
const StatisticsBot = lazy(
  () => import('components/AIChatBotComponents/BotsStatistics/Statistics/Statistics'),
);
const EditAiChatBot = lazy(() => import('pages/Dashboard/EditAiChatBot/EditAiChatBot'));
const CreateBot = lazy(() => import('pages/Dashboard/CreateBot/CreateBot'));
const CodesInfo = lazy(
  () => import('components/AIChatBotComponents/ListCodes/CodesInfo/CodesInfo'),
);
const AddNewTemplatePhoto = lazy(
  () => import('pages/Dashboard/AddNewTemplatePhoto/AddNewTemplatePhoto'),
);
const AddNewTemplateText = lazy(
  () => import('pages/Dashboard/AddNewTemplateText/AddNewTemplateText'),
);

const PrivateRoutes = {
  path: '/',
  element: <AuthLayout />,
  children: [
    {
      element: (
        <ProtectedRoute
          roles={[
            'owner',
            'admin',
            'user_manager',
            'help_desk',
            'billing_manager',
            'manager',
            'user',
            'help',
          ]}
        >
          {withSuspense(<Dashboard />)}
        </ProtectedRoute>
      ),
      path: ELinksName.DASHBOARD + EParams.TAB,
    },
    {
      element: withSuspense(<Channels />),
      path: ELinksName.CHANNELS + EParams.TAB,
    },
    {
      element: withSuspense(<PostDetails />),
      path: ELinksName.POST_DETAILS + EParams.TAB + EParams.ID,
    },
    {
      element: withSuspense(<MediaAndFilesDetails />),
      path: ELinksName.MEDIA_DETAILS + EParams.CHANNEL_ID + EParams.ID + EParams.TAB,
    },
    {
      element: withSuspense(<NameExemple />),
      path: ELinksName.NAME_EXEMPLE + EParams.ID,
    },
    {
      element: withSuspense(<Groups />),
      path: ELinksName.GROUPS + EParams.TAB,
    },
    {
      element: withSuspense(<GroupsDetails />),
      path: ELinksName.GROUPS_DETAILS + EParams.ID + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: <ProtectedRoute roles={['owner']}>{withSuspense(<Admins />)}</ProtectedRoute>,
      path: ELinksName.ADMINS + EParams.TAB,
    },
    {
      element: <ProtectedRoute roles={['owner']}>{withSuspense(<AdminDetails />)}</ProtectedRoute>,
      path: ELinksName.ADMINS_DETAILS + EParams.ID,
    },
    {
      element: withSuspense(<Finance />),
      path: ELinksName.FINANCE + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<Monetization />),
      path: ELinksName.MONETIZATION + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<MonetizationDetailsPage />),
      path: ELinksName.MONETIZATION_DETAILS,
    },
    {
      element: withSuspense(<AdvertisingAnalytics />),
      path: ELinksName.ADVERTISING_ANALYTICS + EParams.TAB,
    },
    {
      element: withSuspense(<Transaction />),
      path: ELinksName.TRANSACTIONS + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<UserDetails />),
      path: ELinksName.USER_DETAILS + EParams.ID + EParams.USER_ID + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<ChannelsDetailsUser />),
      path:
        ELinksName.USERS_USER_DETAILS + EParams.ID + EParams.USER_ID + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<Users />),
      path: ELinksName.USERS + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<ChannelsDetailsUser />),
      path:
        ELinksName.CHANNEL_DETAIL_USER +
        EParams.ID +
        EParams.USER_ID +
        EParams.TAB +
        EParams.SUBTAB,
    },
    {
      element: withSuspense(<AiServices />),
      path: ELinksName.AI_SERVICES + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<Reports />),
      path: ELinksName.REPORTS + EParams.TAB,
    },
    {
      element: withSuspense(<ReportsDetails />),
      path: ELinksName.REPORT_DETAILS + EParams.ID + EParams.TAB,
    },
    {
      element: withSuspense(<Reserved />),
      path: ELinksName.RESERVED_USERNAMES + EParams.TAB,
    },
    {
      element: withSuspense(<Content />),
      path: ELinksName.CONTENT + EParams.TAB,
    },
    {
      element: withSuspense(<TrackDetails />),
      path: ELinksName.TRACK_DETAILS,
    },
    {
      element: withSuspense(<EditTrack />),
      path: ELinksName.EDIT_TRACK + EParams.ID,
    },
    {
      element: withSuspense(<SystemChannels />),
      path: ELinksName.SYSTEM_CHANNELS,
    },
    {
      element: withSuspense(<TermManagement />),
      path: ELinksName.TERMS_MANAGEMENT,
    },
    {
      element: withSuspense(<TermManagementChange />),
      path: ELinksName.TERMS_MANAGEMENT_CHANGE + EParams.TAB,
    },
    {
      element: withSuspense(<AlbumDetails />),
      path: ELinksName.ALBUM_DETAILS,
    },
    {
      element: withSuspense(<ElloPartners />),
      path: ELinksName.ELLO_PARTNERS + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<RequestDetails />),
      path: ELinksName.REQUEST_DETAILS,
    },
    {
      element: withSuspense(<ManagementDetails />),
      path: ELinksName.MANAGEMENT_DETAILS_PERSONAL_DATA + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<RegistrationRequests />),
      path: ELinksName.REGISTRATION_REQUESTS + EParams.TAB,
    },
    {
      element: withSuspense(<ApkFile />),
      path: ELinksName.APK,
    },
    {
      element: withSuspense(<UserPartner />),
      path: ELinksName.USER_PARTNER + EParams.TAB + EParams.SUBTAB,
    },
    {
      element: withSuspense(<AiChatBot />),
      path: ELinksName.AI_CHAT_BOT + EParams.TAB + EParams.ID,
    },
    {
      element: withSuspense(<Reactions />),
      path: ELinksName.REACTIONS + EParams.TAB,
    },
    {
      element: withSuspense(<AddReaction />),
      path: ELinksName.REACTIONS_ADD + EParams.TAB,
    },
    {
      element: withSuspense(<AddReaction />),
      path: ELinksName.REACTIONS_EDIT + EParams.TAB,
    },
    {
      element: withSuspense(<StatisticsBot />),
      path: ELinksName.AI_CHAT_BOT_STATISTICS + EParams.ID + EParams.NAME,
    },
    {
      element: withSuspense(<EditAiChatBot />),
      path: ELinksName.EDIT_BOT + EParams.ID + EParams.NAME + EParams.TAB,
    },
    {
      element: withSuspense(<CreateBot />),
      path: ELinksName.CREATE_BOT + EParams.NAME + EParams.TAB,
    },
    {
      element: withSuspense(<CodesInfo />),
      path: ELinksName.CODES_INFO + EParams.ID + EParams.NAME,
    },
    {
      element: withSuspense(<AddNewTemplateText />),
      path: ELinksName.CREATE_TEXT_TEMPLATE + EParams.ID,
    },
    {
      element: withSuspense(<AddNewTemplatePhoto />),
      path: ELinksName.CREATE_IMAGE_TEMPLATE + EParams.ID,
    },
    // {
    //   element: <ResetPassword />,
    //   path: ELinksName.INVITE_LINK,
    // },
    {
      element: <Navigate to={ELinksName.DASHBOARD + EParams.TAB} replace />,
      path: '/',
    },
    ErrorRoutes,
  ],
};

export default PrivateRoutes;
